import React, { useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import styles from "../../styles/shap_of_you.module.scss";
import cemeraNeeded from "../../assets/images/windown.png";
import { PermissionDeniedScreenProps } from "./types";

const PermissionDeniedScreen: React.FC<PermissionDeniedScreenProps> = ({
  isAuthenticated,
  onExit,
}) => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  // Handle "Restart Experience" button click with consent checking
  const handleRestart = async () => {
    setLoading(true);

    try {
      if (isAuthenticated) {
        // User is authenticated - check consent status via API
        const { data } = await httpClient.get(endpoints.auth.getConsentForm());

        if (data.statusText === "accepted") {
          // Scenario 1: Consent already accepted - skip TC, go to welcome
          console.log("Consent already accepted, skipping TC screen");
          localStorage.setItem("pendingNavigation", "welcome");
          if (onExit) {
            onExit(); // Exit PhotoCaptureFlow, main page will handle navigation
          }
        } else {
          // Scenario 2: Consent rejected - show TC screen
          localStorage.setItem("pendingNavigation", "tc");
          if (onExit) {
            onExit(); // Exit PhotoCaptureFlow, main page will handle navigation
          }
        }
      } else {
        // Scenario 3: User is NOT authenticated - go to TC without API call
        localStorage.setItem("pendingNavigation", "tc");
        if (onExit) {
          onExit(); // Exit PhotoCaptureFlow, main page will handle navigation
        }
      }
    } catch (error) {
      console.error("Error checking consent status:", error);
      // If API fails, show TC screen anyway
      localStorage.setItem("pendingNavigation", "tc");
      if (onExit) {
        onExit();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseWindow = () => {
    router.push("/");
  };

  return (
    <div className="d-none d-md-block w-100">
      <div className={styles.wrapper_denied_camera_access}>
        <div className="container">
          <div className="row align-items-center h-100">
            <div className="col-md-7">
              <div className="ps-xl-5 ms-xl-5 pe-xl-5">
                <div className={styles.illustration}>
                  <Image
                    src={cemeraNeeded}
                    alt="Camera Access"
                    width={508}
                    height={745}
                  />
                </div>
              </div>
            </div>

            <div className="col-md-5">
              <div className={styles.content}>
                <h3>Camera Access Needed</h3>
                <p className="mb-md-5 ">
                  Oops! We need your camera access to bring this experience to
                  life.
                </p>

                <div>
                  <button
                    className={styles.ctaBtnp}
                    onClick={handleRestart}
                    disabled={loading}
                    style={
                      loading
                        ? {
                            background: "#d3d3d3",
                            color: "#888",
                            cursor: "not-allowed",
                            opacity: 0.6,
                          }
                        : {}
                    }
                  >
                    {loading ? "Loading..." : "Restart Experience"}
                    <svg
                      className="ms-1"
                      width="20"
                      height="20"
                      viewBox="0 0 30 30"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.19386 3.64893C7.82334 1.37532 11.251 0 15 0C23.2842 0 30 6.71572 30 15C30 18.2041 28.9953 21.1737 27.2838 23.6109L22.5 15H27C27 8.37258 21.6275 3 15 3C11.7747 3 8.84665 4.2724 6.69034 6.34263L5.19386 3.64893ZM24.8061 26.3511C22.1766 28.6246 18.749 30 15 30C6.71572 30 0 23.2842 0 15C0 11.7958 1.00469 8.82624 2.7162 6.38916L7.5 15H3C3 21.6275 8.37258 27 15 27C18.2253 27 21.1533 25.7276 23.3097 23.6574L24.8061 26.3511Z"
                        fill={loading ? "#888" : "white"}
                      />
                    </svg>
                  </button>
                  <br />
                  <button
                    className={styles.link}
                    onClick={handleCloseWindow}
                    disabled={loading}
                  >
                    Close Window{" "}
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 36 36"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M27 9L9 27"
                        stroke="#262121"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M9 9L27 27"
                        stroke="#262121"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionDeniedScreen;
